import { useCallback, useEffect, useState ,useRef} from "react";
import styled from "styled-components";
import { useStopwatch } from "react-timer-hook";

import Controller from "../components/Controller";
import ErrorBanner from "../components/ErrorBanner";
import Img from "../components/Img";
import Plots from "../components/Plots";
import Toggler from "../components/Toggler";
import { useFile } from "../Contexts";
import ReactLoading from "react-loading";

import download from "../assets/download2.svg";
import play from "../assets/play4.svg";
import plus from "../assets/plus.svg";

// ── Constants ────────────────────────────────────────────────────────────────

const API_BASE = "/api";
const USE_LAMBDA = true; // false: local simulation; true: AWS Lambda
const RUN_ENDPOINT = `${API_BASE}/${USE_LAMBDA ? "run-script-lambda" : "run-script"}`;

const TABS = ["Models", "Executed Models", "Results"];

const OUTPUT_FORMAT_OPTIONS = [
  { value: 1, label: "All runs + summary" },
  { value: 2, label: "Summary only" },
  // { value: 3, label: "3 — Transpose format" },
  // { value: 4, label: "4 — Truth tables + model" },
  // { value: 5, label: "5 — Model w/ update fn" },
  // { value: 6, label: "6 — Truth tables" },
  { value: 7, label: "Event traces" },
];

const FIXED_UPDATES_SCHEMES = ["fixed_updates"];

// ── Styled Components ────────────────────────────────────────────────────────

const TogglerContainer = styled.div`
  margin: 1rem auto 0;
  width: 100%;
`;

const Files = styled.ul`
  margin-top: 16px;
  list-style: none;
  padding: 0;
  display: grid;
  gap: 12px;
  width: 100%;
`;

const FileItem = styled.li`
  display: flex;
  flex-direction: column;
  gap: 10px;
  background: #fff;
  border: 1px solid #eeeeee;
  border-radius: 12px;
  padding: 12px 14px;

  .name {
    color: black;
    font-weight: 600;
    font-size: 14px;
  }

  &:hover {
    border-color: #334155;
  }
`;

const FileHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const ParamSection = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px 16px;
  align-items: center;
  padding: 8px 10px;
  background: #f8fafc;
  border-radius: 8px;
  border: 1px solid #e8edf3;
  width: 100%;
`;

const SectionLabel = styled.div`
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #64748b;
  width: 100%;
  margin-bottom: 2px;
`;

const ParamGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
`;

const baseButtonStyles = `
  border-radius: 24px;
  font-size: 12px;
  color: white;
  font-weight: 600;
  padding: 6px 12px;
  border: none;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 60px;

  > div { margin: 0 10px; }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
`;

const LoadingButton = styled.button`
  ${baseButtonStyles}
  background-color: ${({ $state }) =>
    $state === "running" ? "#ccc" : $state === "run" ? "#22c55e" : "#9ca3af"};
`;

const RunButton = styled.button`
  ${baseButtonStyles}
  background-color: ${({ $state }) =>
    $state === "running"
      ? "#ef9c44"
      : $state === "run"
        ? "#3693fe"
        : "#9ca3af"};
`;

const AddMoreButton = styled.button`
  border-radius: 24px;
  font-size: 12px;
  color: white;
  font-weight: 600;
  padding: 6px 12px;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  margin: 8px;
  background-color: #208df7;

  > img {
    margin: 0 10px;
  }
`;

const DownloadButton = styled.button`
  padding: 5px;
  border-radius: 100%;
  border: none;
  cursor: pointer;
  font-weight: 600;
`;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 20px;
  width: 100%;
  border: 1px solid #ccc;
  border-radius: 12px;
  background: #fafafa;
`;

const Label = styled.label`
  font-weight: 600;
  font-size: 14px;
  color: #222;
  margin: 0 10px;
`;

const Input = styled.input`
  padding: 8px 12px;
  border: 1px solid #ccc;
  border-radius: 8px;
  font-size: 14px;
`;

const UploadArea = styled.div`
  border: 2px dashed #aaa;
  border-radius: 12px;
  padding: 30px;
  text-align: center;
  cursor: pointer;
  background: #fff;

  &:hover {
    border-color: #3b82f6;
    background: #f0f9ff;
  }
`;

const ExecutedFile = styled.div`
  display: flex;
  flex-direction: column;
  padding: 20px;
  border: 1px dashed #d4d6d7;
  margin: 8px 0;
  border-radius: 8px;
  width: 80%;
  background-color: ${({ $ismanual }) => ($ismanual ? "#daf0ff" : "#c5c9d4")};
`;

const ExecutedFileItem = styled.div`
  display: flex;
  background-color: #fff;
  padding: 12px;
  border-radius: 8px;
  justify-content: space-between;
  border: 1px solid #e8e8e8;
`;

const FileName = styled.div`
  max-width: 300px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const InlineLabel = styled.label`
  font-size: 11px;
  color: #475569;
  font-weight: 500;
  white-space: nowrap;
`;

const SmallInput = styled.input`
  padding: 4px 6px;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  font-size: 12px;
  background: #fff;
  color: #1e293b;
  &:disabled {
    opacity: 0.5;
  }
`;

const SmallSelect = styled.select`
  padding: 4px 6px;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  font-size: 12px;
  background: #fff;
  color: #1e293b;
  &:disabled {
    opacity: 0.5;
  }
`;

const CheckLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  color: #475569;
  font-weight: 500;
  cursor: pointer;
  white-space: nowrap;

  input[type="checkbox"] {
    accent-color: #3693fe;
    width: 14px;
    height: 14px;
    cursor: pointer;
  }
`;

const CenteredColumn = styled.div`
  width: 80%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  margin: auto;
  padding: 0 24px;
`;

const ErrorWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  margin: auto;
  width: 80%;
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  width: 100%;
`;

const EmptyIcon = styled.div`
  font-size: 44px;
  margin-bottom: 14px;
  opacity: 0.4;
`;

const EmptyTitle = styled.div`
  font-size: 15px;
  font-weight: 700;
  color: #475569;
  margin-bottom: 6px;
`;

const EmptySubtitle = styled.div`
  font-size: 13px;
  color: #94a3b8;
`;
const TooltipBox = styled.div`
  display: ${({ $visible }) => ($visible ? "block" : "none")};
  background: #1e293b;
  color: #fff;
  font-size: 11px;
  font-weight: 400;
  line-height: 1.5;
  border-radius: 8px;
  padding: 8px 12px;
  position: absolute;
  z-index: 999;
  bottom: calc(100% + 6px);
  left: 50%;
  transform: translateX(-50%);
  width: 240px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
  white-space: normal;

  &::after {
    content: "";
    position: absolute;
    top: 100%;
    left: 50%;
    transform: translateX(-50%);
    border: 5px solid transparent;
    border-top-color: #1e293b;
  }
`;

const InfoIcon = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: #94a3b8;
  font-size: 11px;
  padding: 0 3px;
  line-height: 1;
  display: inline-flex;
  align-items: center;

  &:hover { color: #3693fe; }
`;
const TooltipWrapper = styled.div`
  position: relative;
  display: inline-flex;
  align-items: center;
`;
function InfoTooltip({ text }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  return (
    <TooltipWrapper ref={ref}>
      <InfoIcon type="button" onClick={() => setOpen((v) => !v)}>ⓘ</InfoIcon>
      <TooltipBox $visible={open}>{text}</TooltipBox>
    </TooltipWrapper>
  );
}
// ── Helpers ──────────────────────────────────────────────────────────────────
function stripExt(filename) {
  return filename.replace(/\.[^/.]+$/, "");
}
function upsert(arr, match, item) {
  const idx = arr.findIndex(match);
  if (idx !== -1) {
    const updated = [...arr];
    updated[idx] = item;
    return updated;
  }
  return [...arr, item];
}

function downloadBlob(content, filename) {
  const url = window.URL.createObjectURL(
    new Blob([content], { type: "text/plain;charset=utf-8" }),
  );
  Object.assign(document.createElement("a"), {
    href: url,
    download: filename,
  }).click();
  window.URL.revokeObjectURL(url);
}

// ── Sub-components ───────────────────────────────────────────────────────────

function ElapsedTime({ timing }) {
  const { totalSeconds, milliseconds, reset } = useStopwatch({
    autoStart: false,
    interval: 20,
  });

  useEffect(() => {
    const elapsed = timing
      ? Math.max(0, (timing.end ?? performance.now()) - timing.start)
      : 0;
    reset(new Date(Date.now() + elapsed), Boolean(timing && timing.end == null));
  }, [timing, reset]);

  if (!timing) return null;
  const seconds = timing.end == null
    ? totalSeconds + milliseconds / 1000
    : Math.max(0, (timing.end - timing.start) / 1000);
  const status = timing.end == null ? "Running" : timing.failed ? "Failed after" : "Completed in";
  return (
    <span style={{ fontSize: 12, color: "#475569", fontVariantNumeric: "tabular-nums" }}>
      {status} {seconds.toFixed(2)} s
    </span>
  );
}

function ModelList({
  files,
  btnStates,
  inputs,
  handleInputChange,
  handleClick,
  isLoadingSimulator,
  handleScenarioToggle,
  timings,
}) {
  if (!files.length)
    return (
      <EmptyState>
        <EmptyIcon>📂</EmptyIcon>
        <EmptyTitle>No models uploaded yet</EmptyTitle>
        <EmptySubtitle>Upload a model file to get started</EmptySubtitle>
      </EmptyState>
    );

  return (
    <Files>
      {files.map(({ file, scenarios: fileScenarios = [1] }) => {
        const key = file.name;
        const state = btnStates[key] ?? "disable";
        const inp = inputs[key] ?? {};

        const scenarioList = inp.scenarioList ?? fileScenarios;
        const stepVal = inp.steps ?? "";
        const runVal = inp.runs ?? "";
        const scenariosVal = inp.scenarios ?? "";
        const schemeVal = inp.scheme ?? "sync";
        const outputFormat = inp.output_format ?? 1;
        const seedVal = inp.random_seed ?? "";
        const normalize = inp.normalize ?? false;
        const difference = inp.difference ?? false;
        const concatenate = inp.concatenate ?? false;
        const randomizeRun = inp.randomize_each_run ?? false;
        const eventTracesFile = inp.event_traces_file ?? null;
        const roundTrace = inp.round_trace ?? false;
        const timed = inp.timed ?? false;
        const isFixedUpdates = schemeVal === "fixed_updates";

        const onRun = () =>
          state !== "running" &&
          handleClick(key, file, stepVal, runVal, scenariosVal, schemeVal, inp);

        return (
          <FileItem key={key}>
            {/* ── File name + run button ── */}
            <FileHeader>
              <div className="name">{stripExt(file.name)}</div>
              {state === "running" && isLoadingSimulator ? (
                <LoadingButton onClick={onRun} $state={state} disabled>
                  <ReactLoading
                    type="spin"
                    color="#000"
                    width={10}
                    height={10}
                  />
                </LoadingButton>
              ) : (
                <RunButton
                  onClick={onRun}
                  $state={state}
                  disabled={state === "disable" || isLoadingSimulator}
                >
                  <Img src={play} width="15px" />
                </RunButton>
              )}
            </FileHeader>

            {/* ── Required params ── */}
            <ParamSection>
              <SectionLabel>Required</SectionLabel>

              <ParamGroup style={{ flexWrap: "wrap", gap: "4px 10px" }}>
                <InlineLabel>Scenarios</InlineLabel>
                <InfoTooltip text="Select which scenario indices (State Lists) to simulate." />

                {fileScenarios.map((s) => (
                  <CheckLabel key={s}>
                    <input
                      type="checkbox"
                      checked={scenarioList.includes(s)}
                      disabled={isLoadingSimulator}
                      onChange={(e) =>
                        handleScenarioToggle(
                          key,
                          s,
                          e.target.checked,
                          fileScenarios,
                        )
                      }
                    />
                    {s}
                  </CheckLabel>
                ))}
              </ParamGroup>

              <ParamGroup>
                <InlineLabel>Scheme</InlineLabel>
                <InfoTooltip text="Simulation scheme: random (one element per step), round (all elements per round in random order), sync (all elements at each step), fixed_updates (order from event traces file)." />

                <SmallSelect
                  style={{ width: 120 }}
                  value={schemeVal}
                  disabled={isLoadingSimulator}
                  onChange={(e) =>
                    handleInputChange(key, "scheme", e.target.value)
                  }
                >
                  <option value="random">random</option>
                  <option value="round">round</option>
                  <option value="sync">sync</option>
                  {/* <option value="ra_multi">ra_multi</option> */}
                  {/* <option value="sync_multi">sync_multi</option> */}
                  {/* <option value="rand_sync">rand_sync</option> */}
                  {/* <option value="rand_sync_guess">rand_sync_guess</option> */}
                  <option value="fixed_updates">fixed_updates</option>
                </SmallSelect>
              </ParamGroup>

              <ParamGroup>
                <InlineLabel>Steps</InlineLabel>
                <InfoTooltip text="Number of simulation steps (random, sync schemes) or rounds (round scheme)." />

                <SmallInput
                  type="number"
                  placeholder="1000"
                  style={{ width: 70 }}
                  value={stepVal}
                  disabled={isLoadingSimulator}
                  onChange={(e) =>
                    handleInputChange(key, "steps", e.target.value)
                  }
                />
              </ParamGroup>

              <ParamGroup>
                <InlineLabel>Runs</InlineLabel>
                <InfoTooltip text="Number of simulation runs (repetitions)." />

                <SmallInput
                  type="number"
                  placeholder="100"
                  style={{ width: 70 }}
                  value={runVal}
                  disabled={isLoadingSimulator}
                  onChange={(e) =>
                    handleInputChange(key, "runs", e.target.value)
                  }
                />
              </ParamGroup>
            </ParamSection>

            {/* ── Optional params ── */}
            <ParamSection>
              <SectionLabel>Optional</SectionLabel>

              <ParamGroup>
                <InlineLabel>Output Format</InlineLabel>
                <InfoTooltip text="Format of trace file output. Format 1: all runs + summary. Format 2: summary only. Format 3: event traces showing elements updated at each step." />

                <SmallSelect
                  style={{ width: 190 }}
                  value={outputFormat}
                  disabled={isLoadingSimulator}
                  onChange={(e) =>
                    handleInputChange(
                      key,
                      "output_format",
                      Number(e.target.value),
                    )
                  }
                >
                  {OUTPUT_FORMAT_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </SmallSelect>
              </ParamGroup>
              {/* 
              <ParamGroup>
                <InlineLabel>Random Seed</InlineLabel>
                <SmallInput
                  type="number"
                  placeholder="optional"
                  style={{ width: 80 }}
                  value={seedVal}
                  disabled={isLoadingSimulator}
                  onChange={(e) => handleInputChange(key, "random_seed", e.target.value)}
                />
              </ParamGroup> */}

              <CheckLabel title="Normalize values to [0,1] in output trace file">
                <input
                  type="checkbox"
                  checked={normalize}
                  disabled={isLoadingSimulator}
                  onChange={(e) =>
                    handleInputChange(key, "normalize", e.target.checked)
                  }
                />
                Normalize
              </CheckLabel>
              <InfoTooltip text="Normalize values to the range [0,1] in the output trace file." />

              <CheckLabel title="Calculate difference between scenarios">
                <input
                  type="checkbox"
                  checked={difference}
                  disabled={isLoadingSimulator}
                  onChange={(e) =>
                    handleInputChange(key, "difference", e.target.checked)
                  }
                />
                Difference
              </CheckLabel>
              <InfoTooltip text="Calculate the difference between traces in the first scenario and other scenarios." />

              <CheckLabel title="Concatenate trace files for all scenarios">
                <input
                  type="checkbox"
                  checked={concatenate}
                  disabled={isLoadingSimulator}
                  onChange={(e) =>
                    handleInputChange(key, "concatenate", e.target.checked)
                  }
                />
                Concatenate
              </CheckLabel>
              <InfoTooltip text="Concatenate trace files for all scenarios into one file." />

              <CheckLabel title="Randomize initial values in each run">
                <input
                  type="checkbox"
                  checked={randomizeRun}
                  disabled={isLoadingSimulator}
                  onChange={(e) =>
                    handleInputChange(
                      key,
                      "randomize_each_run",
                      e.target.checked,
                    )
                  }
                />
                Randomize Each Run
              </CheckLabel>
              <InfoTooltip text="Randomizes initial values in each run." />

              <CheckLabel title="Save values at end of each round (round scheme only)">
                <input
                  type="checkbox"
                  checked={roundTrace}
                  disabled={isLoadingSimulator || schemeVal !== "round"}
                  onChange={(e) =>
                    handleInputChange(key, "round_trace", e.target.checked)
                  }
                />
                <span style={{ opacity: schemeVal !== "round" ? 0.4 : 1 }}>
                  Round Trace
                </span>
              </CheckLabel>
              <InfoTooltip text="Save the values at the end of each round. Only applicable for the round scheme. " />

              <CheckLabel title="Show elapsed time while the simulation request runs">
                <input
                  type="checkbox"
                  checked={timed}
                  disabled={isLoadingSimulator}
                  onChange={(e) =>
                    handleInputChange(key, "timed", e.target.checked)
                  }
                />
                Timed
              </CheckLabel>
              <InfoTooltip text="Show elapsed time from sending the request until results are ready, including simulation and result processing." />
              {timed && <ElapsedTime timing={timings[key]} />}
            </ParamSection>

            {/* ── Event Traces File (required for fixed_updates) ── */}
            {isFixedUpdates && (
              <ParamSection
                style={{
                  borderColor: eventTracesFile ? "#86efac" : "#fca5a5",
                  background: eventTracesFile ? "#f0fdf4" : "#fff5f5",
                }}
              >
                <SectionLabel style={{ color: "#b91c1c" }}>
                  ⚠ Event Traces File — Required for fixed_updates scheme
                </SectionLabel>
                <ParamGroup style={{ width: "100%" }}>
                  <InlineLabel>File:</InlineLabel>
                  <SmallInput
                    type="file"
                    accept=".txt"
                    disabled={isLoadingSimulator}
                    style={{ width: "auto", flex: 1 }}
                    onChange={(e) => {
                      const file = e.target.files?.[0] ?? null;
                      handleInputChange(key, "event_traces_file", file);
                    }}
                  />
                  {eventTracesFile && (
                    <span
                      style={{
                        fontSize: 11,
                        color: "#16a34a",
                        fontWeight: 600,
                      }}
                    >
                      ✓ {eventTracesFile.name}
                    </span>
                  )}
                </ParamGroup>
              </ParamSection>
            )}
          </FileItem>
        );
      })}
    </Files>
  );
}

const FORMAT_LABELS = {
  1: "All runs + summary",
  2: "Summary only",
  // 3: "Transpose format",
  // 4: "Truth tables + model",
  // 5: "Model w/ update fn",
  // 6: "Truth tables",
  7: "Event traces",
};

function ExecutedModels({ executedFile }) {
  const fmt = executedFile.output_format ?? 1;
  const fmtLabel = FORMAT_LABELS[fmt] ?? "All runs + summary";

  return (
    <ExecutedFile>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          marginBottom: 10,
          flexWrap: "wrap",
        }}
      >
        <div style={{ fontWeight: 800 }}>{stripExt(executedFile.filename)}</div>
        <div
          style={{
            fontSize: 11,
            fontWeight: 600,
            background: "#dbeeff",
            color: "#003594",
            border: "1px solid #bfdbfe",
            borderRadius: 20,
            padding: "2px 10px",
            whiteSpace: "nowrap",
          }}
        >
          Format {fmt} — {fmtLabel}
        </div>
      </div>
      {executedFile.files.map((file) => (
        <ExecutedFileItem key={file.filename}>
          <div className="meta">
            <FileName>{file.filename}</FileName>
          </div>
          <DownloadButton
            onClick={() => downloadBlob(file.content, file.filename)}
          >
            <Img src={download} width="15" />
          </DownloadButton>
        </ExecutedFileItem>
      ))}
    </ExecutedFile>
  );
}

function ManualModelList({ manualModel, isManual = false }) {
  if (!manualModel.length)
    return (
      <EmptyState>
        <EmptyIcon>📄</EmptyIcon>
        <EmptyTitle>No trace files</EmptyTitle>
        <EmptySubtitle>Add trace files to view results</EmptySubtitle>
      </EmptyState>
    );

  return (
    <ExecutedFile $ismanual={isManual}>
      <div style={{ marginBottom: 10, fontWeight: 800 }}>
        {stripExt(manualModel.filename)}
      </div>
      {manualModel.map((file) => (
        <ExecutedFileItem key={file.name}>
          <div className="meta">
            <FileName>{file.name}</FileName>
          </div>
        </ExecutedFileItem>
      ))}
    </ExecutedFile>
  );
}

function UploadTraceFiles({ onSubmitted, manualModel, setManualModel }) {
  const [uploadForm, setUploadForm] = useState(false);
  const [fileName, setFileName] = useState("");
  const [fileCount, setFileCount] = useState(null);
  const [files, setFiles] = useState([]);

  const { setExecutedFile, setResults } = useFile();

  const handleFiles = (e) => {
    const picked = Array.from(e.target.files ?? []);
    const txtOnly = picked.filter((f) => f.name.toLowerCase().endsWith(".txt"));
    if (txtOnly.length !== picked.length) alert("Only .txt files are allowed.");
    setFiles((prev) => {
      const limit = Number(fileCount);
      const remaining =
        limit && !Number.isNaN(limit) ? limit - prev.length : Infinity;
      if (remaining <= 0) {
        alert(`No more than ${limit} file(s) allowed.`);
        return prev;
      }
      return [...prev, ...txtOnly.slice(0, remaining)];
    });
    e.target.value = "";
  };

  const handleRemoveFile = (index) =>
    setFiles((prev) => prev.filter((_, i) => i !== index));

  const mergeIntoState = (setFn, matchKey, matchVal, item) =>
    setFn((prev) => upsert(prev, (x) => x[matchKey] === matchVal, item));

  const handleAddedFile = async () => {
    const count = Number(fileCount);
    if (!fileName.trim()) return alert("Please enter a model name.");
    if (!count || Number.isNaN(count) || count < 1)
      return alert("Please enter a valid number of scenarios (≥ 1).");
    if (files.length !== count)
      return alert(
        `Please select exactly ${count} file(s). Currently selected: ${files.length}.`,
      );

    const formData = new FormData();
    files.forEach((f) => formData.append("files", f));

    try {
      const res = await fetch(`${API_BASE}/process-traces`, {
        method: "POST",
        body: formData,
      });
      if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
      const data = await res.json();
      const model = fileName.trim();
      mergeIntoState(setExecutedFile, "filename", model, {
        files: data?.trace_files,
        filename: model,
      });
      mergeIntoState(setResults, "model", model, {
        model,
        data: data?.traces_data?.data,
      });
    } catch (err) {
      console.error("Upload error:", err);
      setManualModel((prev) => [
        ...prev,
        { files, scenarios: count, filename: fileName.trim() },
      ]);
    }

    setUploadForm(false);
    setFileName("");
    setFileCount(null);
    setFiles([]);
    onSubmitted?.();
  };

  return (
    <CenteredColumn style={{ width: "80%" }}>
      {manualModel.map((model, idx) => (
        <ManualModelList
          key={`${model.filename}-${idx}`}
          manualModel={model.files}
          filename={model.filename}
        />
      ))}

      {uploadForm && (
        <Wrapper>
          <div style={{ display: "flex", gap: 16 }}>
            <div>
              <Label>Model Name</Label>
              <Input
                type="text"
                placeholder="Enter file name"
                value={fileName}
                onChange={(e) => setFileName(e.target.value)}
              />
            </div>
            <div>
              <Label>Number of Scenarios</Label>
              <Input
                type="number"
                min="1"
                value={fileCount ?? ""}
                onChange={(e) =>
                  setFileCount(
                    e.target.value === "" ? null : Number(e.target.value),
                  )
                }
              />
            </div>
          </div>

          <UploadArea
            onClick={() => document.getElementById("fileInput").click()}
          >
            {files.length === 0
              ? "Click or drag files here to upload"
              : `${files.length} file(s) selected`}
          </UploadArea>

          <input
            id="fileInput"
            type="file"
            multiple
            hidden
            accept=".txt"
            onChange={handleFiles}
          />

          {files.length > 0 && (
            <ul style={{ marginTop: 10, paddingLeft: 0, listStyle: "none" }}>
              {files.map((file, index) => (
                <li
                  key={index}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    marginBottom: 6,
                    border: "1px solid #ccc",
                    padding: "6px 10px",
                    borderRadius: 6,
                  }}
                >
                  <span>{file.name}</span>
                  <button
                    type="button"
                    onClick={() => handleRemoveFile(index)}
                    style={{
                      marginLeft: 10,
                      background: "red",
                      color: "white",
                      border: "none",
                      borderRadius: 4,
                      cursor: "pointer",
                      padding: "2px 6px",
                    }}
                  >
                    ✕
                  </button>
                </li>
              ))}
            </ul>
          )}

          <button
            onClick={handleAddedFile}
            disabled={!fileName || !fileCount || !files.length}
          >
            Submit
          </button>
        </Wrapper>
      )}

      <AddMoreButton onClick={() => setUploadForm(true)}>
        <Img src={plus} />
        Add Trace files
      </AddMoreButton>
    </CenteredColumn>
  );
}

// ── Main Component ───────────────────────────────────────────────────────────

export default function Simulator() {
  const [activeTab, setActiveTab] = useState("Models");
  const [error, setError] = useState(null);
  const [pickedScenario, setPickedScenario] = useState([]);
  const [pickedElement, setPickedElement] = useState([]);
  const [pickedModel, setPickedModel] = useState([]);
  const [isLoadingSimulator, setIsLoadingSimulator] = useState(false);
  const [btnStates, setBtnStates] = useState({});
  const [inputs, setInputs] = useState({});
  const [timings, setTimings] = useState({});
  const [manualModel, setManualModel] = useState([]);
  const {
    files,
    executedFile,
    setExecutedFile,
    notify,
    setNotify,
    setResults,
    results,
  } = useFile();
  useEffect(() => {
    files.forEach(({ file, scenarios: fileScenarios = [1] }) => {
      const key = file.name;
      setInputs((prev) => {
        // Only initialize if not already set
        if (prev[key]?.scenarioList) return prev;
        return {
          ...prev,
          [key]: {
            ...prev[key],
            scenarioList: fileScenarios, // all checked by default
          },
        };
      });
    });
  }, [files]);
  const handleScenarioToggle = (key, scenario, checked, allScenarios) => {
    setInputs((prev) => {
      const current = prev[key]?.scenarioList ?? allScenarios; // default = all
      const next = checked
        ? [...new Set([...current, scenario])].sort((a, b) => a - b)
        : current.filter((s) => s !== scenario);

      const updated = { ...prev, [key]: { ...prev[key], scenarioList: next } };
      const { steps, runs } = updated[key] ?? {};
      setButtonState(
        key,
        steps > 0 && runs > 0 && next.length > 0 ? "run" : "disable",
      );
      return updated;
    });
  };
  const setButtonState = (key, state) =>
    setBtnStates((prev) => ({ ...prev, [key]: state }));

  const handleInputChange = (key, field, value) => {
    setInputs((prev) => {
      const next = { ...prev, [key]: { ...prev[key], [field]: value } };
      const { steps, runs, scenarioList } = next[key] ?? {};
      const ready = steps > 0 && runs > 0 && (scenarioList?.length ?? 0) > 0;
      setButtonState(key, ready ? "run" : "disable");
      return next;
    });
  };

  const handleClick = async (
    key,
    file,
    steps,
    runs,
    scenarios,
    scheme,
    extraInputs = {},
  ) => {
    // Validate fixed_updates requires event traces file
    if (scheme === "fixed_updates" && !extraInputs?.event_traces_file) {
      setError(
        "The fixed_updates scheme requires an Event Traces File. Please upload one in the Optional section.",
      );
      return;
    }

    setNotify(false);
    setButtonState(key, "running");
    setIsLoadingSimulator(true);
    setError(null);

    const timingStart = extraInputs.timed ? performance.now() : null;
    let completed = false;
    setTimings((prev) => ({
      ...prev,
      [key]: timingStart == null ? null : { start: timingStart },
    }));

    try {
      // Use FormData if we have a file attachment, otherwise JSON
      let res;
      if (extraInputs?.event_traces_file) {
        const formData = new FormData();
        formData.append("filename", file.name);
        formData.append("steps", String(steps ?? 1));
        formData.append("runs", String(runs ?? 1));
        formData.append(
          "scenarios",
          JSON.stringify(
            (extraInputs?.scenarioList ?? [1])
              .map(Number)
              .filter((n) => n > 0)
              .map(String),
          ),
        );
        formData.append("scheme", scheme ?? "sync");
        formData.append(
          "output_format",
          String(extraInputs?.output_format ?? 1),
        );
        formData.append("normalize", String(extraInputs?.normalize ?? false));
        formData.append("difference", String(extraInputs?.difference ?? false));
        formData.append(
          "concatenate",
          String(extraInputs?.concatenate ?? false),
        );
        formData.append(
          "randomize_each_run",
          String(extraInputs?.randomize_each_run ?? false),
        );
        formData.append(
          "round_trace",
          String(extraInputs?.round_trace ?? false),
        );
        formData.append("timed", String(extraInputs?.timed ?? false));
        if (extraInputs?.random_seed)
          formData.append("random_seed", String(extraInputs.random_seed));
        formData.append("event_traces_file", extraInputs.event_traces_file);
        res = await fetch(RUN_ENDPOINT, {
          method: "POST",
          body: formData,
        });
      } else {
        res = await fetch(RUN_ENDPOINT, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            filename: file.name,
            steps: steps ?? 1,
            runs: runs ?? 1,

            scheme: scheme ?? "sync",
            output_format: extraInputs?.output_format ?? 1,
            normalize: extraInputs?.normalize ?? false,
            difference: extraInputs?.difference ?? false,
            concatenate: extraInputs?.concatenate ?? false,
            randomize_each_run: extraInputs?.randomize_each_run ?? false,
            round_trace: extraInputs?.round_trace ?? false,
            timed: extraInputs?.timed ?? false,
            random_seed: extraInputs?.random_seed || null,
            scenarios: (extraInputs?.scenarioList ?? [1])
              .map(Number)
              .filter((n) => n > 0)
              .map(String),
          }),
        });
      }

      const text = await res.text();
      let data;
      try {
        data = JSON.parse(text);
      } catch {
        throw new Error(
          res.ok
            ? "Server returned invalid response format"
            : `Server error (${res.status}): ${text.substring(0, 200)}`,
        );
      }

      if (!res.ok)
        throw new Error(data?.detail || data?.error || `HTTP ${res.status}`);
      if (!data?.success) throw new Error(data?.error || "Simulation failed");

      const fmt = extraInputs?.output_format ?? 1;
      const runKey = `${file.name}__fmt${fmt}`;

      setExecutedFile((prev) =>
        upsert(prev, (x) => x.key === runKey, {
          key: runKey,
          filename: file.name,
          files: data.trace_files,
          output_format: fmt,
        }),
      );
      setResults((prev) =>
        upsert(prev, (x) => x.key === runKey, {
          key: runKey,
          model: runKey,
          data: data.traces_data.data,
        }),
      );

      setButtonState(key, "run");
      setNotify(true);
      setTimeout(() => setNotify(false), 4000);
      completed = true;
    } catch (err) {
      setError(err.message || "An unexpected error occurred");
      setButtonState(key, "run");
    } finally {
      if (timingStart != null) {
        const timingEnd = performance.now();
        setTimings((prev) => ({
          ...prev,
          [key]: { start: timingStart, end: timingEnd, failed: !completed },
        }));
      }
      setIsLoadingSimulator(false);
    }
  };

  const clearError = useCallback(() => setError(null), []);

  return (
    <CenteredColumn>
      <TogglerContainer>
        <Toggler
          setNotify={setNotify}
          values={TABS}
          onChange={(tab) => {
            setActiveTab(tab);
            if (tab === "Executed Models") setNotify(false);
          }}
          currentValue={activeTab}
          notify={notify}
        />
      </TogglerContainer>

      {error && (
        <ErrorWrapper>
          <ErrorBanner>
            <span className="error-icon">⚠️</span>
            <div className="error-text">{error}</div>
            <button
              className="close-btn"
              onClick={clearError}
              aria-label="Close error"
            >
              ×
            </button>
          </ErrorBanner>
        </ErrorWrapper>
      )}

      {activeTab === "Models" && (
        <ModelList
          files={files}
          btnStates={btnStates}
          inputs={inputs}
          handleInputChange={handleInputChange}
          handleClick={handleClick}
          isLoadingSimulator={isLoadingSimulator}
          handleScenarioToggle={handleScenarioToggle}
          timings={timings}
        />
      )}

      {activeTab === "Executed Models" && (
        <>
          {executedFile?.length > 0 ? (
            executedFile.map((ef, i) => (
              <ExecutedModels key={ef.key || i} executedFile={ef} />
            ))
          ) : (
            <EmptyState>
              <EmptyIcon>🧬</EmptyIcon>
              <EmptyTitle>No executed models yet</EmptyTitle>
              <EmptySubtitle>
                Run a simulation from the Models tab first
              </EmptySubtitle>
            </EmptyState>
          )}
          <UploadTraceFiles
            onSubmitted={() => setActiveTab("Executed Models")}
            manualModel={manualModel}
            setManualModel={setManualModel}
          />
        </>
      )}

      {activeTab === "Results" &&
        (() => {
          const plottableModels = results.filter(
            (r) => r.data && Object.keys(r.data).length > 0,
          );
          const hasPlottable = plottableModels.length > 0;

          return (
            <>
              {hasPlottable ? (
                <Controller
                  pickedModel={pickedModel}
                  setPickedModel={setPickedModel}
                  pickedScenario={pickedScenario}
                  setPickedScenario={setPickedScenario}
                  pickedElement={pickedElement}
                  setPickedElement={setPickedElement}
                />
              ) : (
                <EmptyState>
                  <EmptyIcon style={{ opacity: 0.25 }}>📊</EmptyIcon>
                  <EmptyTitle style={{ color: "#94a3b8" }}>
                    No results available
                  </EmptyTitle>
                  <EmptySubtitle>
                    Run a simulation using output format 1 or 3 to view plots
                    here
                  </EmptySubtitle>
                </EmptyState>
              )}
              <div style={{ margin: "auto", width: "80%" }}>
                {pickedScenario.length > 0
                  ? results
                      .filter(
                        (r) =>
                          pickedModel.includes(r.key || r.model) &&
                          r.data &&
                          Object.keys(r.data).length > 0,
                      )
                      .map((item) => (
                        <Plots
                          key={item.model}
                          selected={pickedScenario}
                          pickedElement={pickedElement}
                          pickedModel={item.model}
                          data={item.data}
                        />
                      ))
                  : null}
              </div>
            </>
          );
        })()}
    </CenteredColumn>
  );
}
